import { useState, useEffect } from 'react'
import styles from './AddUserToProjectModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function AddUserToProjectModal({ 
    isOpen, 
    onClose, 
    onSave, 
    project, 
    apiToken 
}) {
    const [formData, setFormData] = useState({
        user_id: '',
        role: 'member'
    })
    const [availableUsers, setAvailableUsers] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingUsers, setIsLoadingUsers] = useState(false)
    const [error, setError] = useState('')

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !isLoading && !isLoadingUsers)

    useEffect(() => {
        if (isOpen) {
            setFormData({ user_id: '', role: 'member' })
            setError('')
            loadAvailableUsers()
        }
    }, [isOpen])

    const loadAvailableUsers = async () => {
        setIsLoadingUsers(true)
        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.request('/teammates?status=active')
            
            console.log('Teammates API response:', response)
            console.log('Project users with access:', project.users_with_access)
            
            if (response.teammates) {
                // Filter out users who already have access to this project
                const currentUserIds = project.users_with_access?.map(u => u.id) || []
                const availableUsers = response.teammates.filter(user => 
                    !currentUserIds.includes(user.id)
                )
                
                console.log('Current user IDs with access:', currentUserIds)
                console.log('Available users after filtering:', availableUsers)
                
                setAvailableUsers(availableUsers)
            } else {
                setError('Failed to load available users')
            }
        } catch (err) {
            console.error('Error loading users:', err)
            setError('Failed to load users')
        } finally {
            setIsLoadingUsers(false)
        }
    }

    if (!isOpen) return null

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsLoading(true)
        setError('')

        try {
            apiClient.setAuthToken(apiToken)
            
            // Debug logging
            console.log('Form submission - formData:', formData)
            console.log('Available users:', availableUsers)
            
            // Simple validation - just check if user_id is not empty string
            if (!formData.user_id || formData.user_id === '') {
                setError('Please select a user')
                setIsLoading(false)
                return
            }
            
            const response = await apiClient.request(`/projects/${project.id}/users`, {
                method: 'POST',
                body: JSON.stringify({
                    user_id: formData.user_id, // Send as string, let backend handle conversion
                    role: formData.role
                })
            })

            if (response.success) {
                const selectedUser = availableUsers.find(u => u.id === parseInt(formData.user_id))
                onSave(response.project, `${selectedUser?.full_name || 'User'} added to project successfully`)
                onClose()
            } else {
                setError(response.error || 'Failed to add user to project')
            }
        } catch (err) {
            console.error('Failed to add user to project:', err)
            setError(err.message || 'Failed to add user. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const selectedUser = availableUsers.find(u => u.id === parseInt(formData.user_id))

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2>Add User to Project</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.projectInfo}>
                        <h3>Project: {project.name}</h3>
                        <p>Adding user access to this project</p>
                    </div>

                    {isLoadingUsers ? (
                        <div className={styles.loadingMessage}>
                            Loading available users...
                        </div>
                    ) : (
                        <>
                            <div className={styles.formGroup}>
                                <label htmlFor="user_id">Select User *</label>
                                <select
                                    id="user_id"
                                    name="user_id"
                                    value={formData.user_id}
                                    onChange={handleChange}
                                    required
                                    disabled={isLoading}
                                >
                                    <option value="">Choose a user...</option>
                                    {availableUsers.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.full_name} ({user.email})
                                        </option>
                                    ))}
                                </select>
                                {availableUsers.length === 0 && (
                                    <div className={styles.helpText}>
                                        All workspace members already have access to this project.
                                    </div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="role">Project Role *</label>
                                <select
                                    id="role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                    required
                                >
                                    <option value="member">Member - Can view and work on tasks</option>
                                    <option value="admin">Admin - Can manage project and tasks</option>
                                </select>
                            </div>

                            {selectedUser && (
                                <div className={styles.userPreview}>
                                    <div className={styles.userInfo}>
                                        <div className={styles.userDetails}>
                                            <strong>{selectedUser.full_name}</strong>
                                            <span>{selectedUser.email}</span>
                                        </div>
                                    </div>
                                    <div className={styles.roleInfo}>
                                        Role: <strong>{formData.role}</strong>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div className={styles.modalActions}>
                        <button 
                            type="button" 
                            className={styles.cancelButton}
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className={styles.saveButton}
                            disabled={isLoading || !formData.user_id || isLoadingUsers || availableUsers.length === 0}
                        >
                            {isLoading ? 'Adding...' : 'Add User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}